import { useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useActivateAccount } from "../../hooks/queries/useAuthQueries";
import { useActivationWizardForm } from "./hooks/useActivationWizardForm";
import { resetPasswordValidators } from "../../lib/validations/authValidations";
import CustomUnauthorized from "../../components/ui/CustomUnauthorized/CustomUnauthorized";
import CustomPasswordInput from "../../components/ui/CustomPasswordInput/CustomPasswordInput";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import AuthLayout from "../../components/auth/authLayout/AuthLayout";
import AuthCard from "../../components/auth/authCard/AuthCard";
import PinSetupFields from "../../components/auth/PinSetupFields/PinSetupFields";
import { toastService } from "../../providers";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { isDeadTokenMessage } from "../../components/auth/config/authActionCodes";

function NewPassword() {
  const [searchParams] = useSearchParams();
  const activationToken = searchParams.get("token");
  const navigate = useNavigate();
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  const { mutate: activateAccount, isPending } = useActivateAccount();
  const {
    step,
    goToStep2,
    goToStep1,
    control,
    handleSubmit,
    passwordValidations,
    isStep1Valid,
    isStep2Valid,
  } = useActivationWizardForm();

  const currentPassword = useWatch({ control, name: "password" });

  const onSubmit = (values) => {
    activateAccount(
      {
        token: activationToken,
        password: values.password,
        confirmPassword: values.confirmPassword,
        newPin: values.newPin,
        confirmPin: values.confirmPin,
      },
      {
        onSuccess: () => {
          toastService.success("Account activated. Proceed to login.");
          navigate("/auth/login");
        },
        onError: (error) => {
          const errorMessage = extractAxiosErrorMessage(error);
          const isExpired = isDeadTokenMessage(errorMessage);

          setIsTokenExpired(isExpired);
          toastService.error(
            isExpired
              ? "Activation token has expired."
              : errorMessage || "Something went wrong! Please try again.",
          );
        },
      },
    );
  };

  if (!activationToken) {
    return (
      <CustomUnauthorized
        leftTitle="Access Denied!"
        leftMessage="Please ensure you clicked the full link from your email."
        actions={[
          {
            label: "Back To Home",
            href: "/",
          },
        ]}
      />
    );
  }

  return (
    <AuthLayout>
      <AuthCard
        title="New Password"
        subtitle={
          <div className="text-black-50 text-xs sm:text-sm">
            {step === 1
              ? "Set up a new password to access your account"
              : "Set up a PIN to finish activating your account"}
          </div>
        }
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          {step === 1 && !isTokenExpired && (
            <>
              <Controller
                name="password"
                control={control}
                rules={{
                  validate: (v) => resetPasswordValidators.password(v) ?? true,
                }}
                render={({ field, fieldState }) => (
                  <CustomPasswordInput
                    label="Password:"
                    radius="md"
                    showStrengthMeter={false}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  validate: (v) =>
                    resetPasswordValidators.confirmPassword(
                      currentPassword,
                      v,
                    ) ?? true,
                }}
                render={({ field, fieldState }) => (
                  <CustomPasswordInput
                    label="Confirm Password:"
                    radius="md"
                    showStrengthMeter={false}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <div className="mt-2 text-xs mb-5">
                <ul className="pl-5 space-y-1 list-none">
                  {passwordValidations.map((rule, idx) => (
                    <li key={idx} className="relative pl-5">
                      <span
                        className={`absolute left-0 top-1/2 -translate-y-1/2 rounded-full w-2 h-2 ${rule.valid ? "bg-green-600" : "bg-red-600"}`}
                      ></span>
                      {rule.label}
                    </li>
                  ))}
                </ul>
              </div>

              <CustomButton
                type="button"
                radius="xl"
                size="sm"
                fullWidth
                disabled={!isStep1Valid}
                onClick={goToStep2}
              >
                Continue
              </CustomButton>
            </>
          )}

          {step === 2 && !isTokenExpired && (
            <>
              <PinSetupFields control={control} disabled={isPending} />

              <CustomButton
                type="submit"
                radius="xl"
                size="sm"
                fullWidth
                disabled={isPending || !isStep2Valid}
                loading={isPending}
              >
                Finish
              </CustomButton>

              <CustomButton
                type="button"
                variant="subtle"
                size="sm"
                fullWidth
                disabled={isPending}
                onClick={goToStep1}
              >
                Back
              </CustomButton>
            </>
          )}

          {isTokenExpired && (
            <CustomButton
              type="button"
              radius="xl"
              size="sm"
              fullWidth
              onClick={() => navigate("/auth/request-reactivation")}
            >
              Request Reactivation
            </CustomButton>
          )}
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default NewPassword;
